import { Controller } from "@hotwired/stimulus"
import { formatCpfCnpj } from "../utils/cpf_cnpj"

export default class extends Controller {
  static targets = [
    "type", "document", "foreign", "corporateField", "individualField",
    "brazilianField", "name", "nickname"
  ]

  static values = {
    documentLabel: { type: String, default: "CPF/CNPJ" },
    foreignDocumentLabel: { type: String, default: "Passaporte" },
    individualNameLabel: { type: String, default: "Nome" },
    corporateNameLabel: { type: String, default: "Razão social" },
    nicknameLabel: { type: String, default: "Apelido" },
    tradeNameLabel: { type: String, default: "Nome fantasia" }
  }

  connect() {
    this.applyMask()
  }

  typeChanged() {
    this.applyMask()
    this.documentTarget.focus()
  }

  documentChanged() {
    this.applyMask()
  }

  foreignChanged() {
    this.applyMask()
    this.documentTarget.focus()
  }

  applyMask() {
    const foreign = this.hasForeignTarget && this.foreignTarget.checked

    this.togglePersonType(foreign)
    const personType = this.typeTarget.value
    this.toggleRegistrationFields(foreign, personType)

    if (foreign) {
      this.documentTarget.removeAttribute("maxlength")
      this.documentTarget.placeholder = this.foreignDocumentLabelValue
      this.documentTarget.inputMode = "text"
      this.setTargetLabel(this.documentTarget, this.foreignDocumentLabelValue)
      return
    }

    const cpf = personType === "fisica"

    this.documentTarget.value = formatCpfCnpj(this.documentTarget.value, personType)
    this.documentTarget.maxLength = cpf ? 14 : 18
    this.documentTarget.placeholder = cpf ? "000.000.000-00" : "00.000.000/0000-00"
    this.documentTarget.inputMode = cpf ? "numeric" : "text"
    this.setTargetLabel(this.documentTarget, this.documentLabelValue)
  }

  togglePersonType(foreign) {
    const typeWrapper = this.typeTarget.closest(".ui-form-table__field-shell")
    const documentWrapper = this.documentTarget.closest(".ui-form-table__field-shell")

    if (foreign) {
      if (!this.foreignMode) this.previousPersonType = this.typeTarget.value
      this.typeTarget.value = "fisica"
    } else if (this.foreignMode && this.previousPersonType) {
      this.typeTarget.value = this.previousPersonType
    }

    if (typeWrapper) typeWrapper.hidden = foreign
    if (documentWrapper) {
      this.documentColumnClasses ||= Array.from(documentWrapper.classList)
        .filter((className) => /^col-md-\d+$/.test(className))

      Array.from(documentWrapper.classList)
        .filter((className) => /^col-md-\d+$/.test(className))
        .forEach((className) => documentWrapper.classList.remove(className))

      const releasedColumns = this.columnSpan(typeWrapper)
      const documentColumns = this.columnSpanFromClasses(this.documentColumnClasses)
      const foreignColumns = Math.min(12, releasedColumns + documentColumns) || 12
      const columnClasses = foreign ? [`col-md-${foreignColumns}`] : this.documentColumnClasses
      documentWrapper.classList.add(...columnClasses)
    }

    this.foreignMode = foreign
  }

  toggleRegistrationFields(foreign, personType) {
    const corporate = !foreign && personType === "juridica"
    const individual = !foreign && personType === "fisica"

    this.setFieldsVisibility(this.corporateFieldTargets, corporate)
    this.setFieldsVisibility(this.individualFieldTargets, individual)
    this.setFieldsVisibility(this.brazilianFieldTargets, !foreign)

    this.setTargetLabel(this.nameTarget, corporate ? this.corporateNameLabelValue : this.individualNameLabelValue)
    if (this.hasNicknameTarget) {
      this.setTargetLabel(this.nicknameTarget, corporate ? this.tradeNameLabelValue : this.nicknameLabelValue)
    }
  }

  setFieldsVisibility(targets, visible) {
    targets.forEach((target) => {
      const wrapper = target.closest(".ui-form-table__field-shell")
      if (wrapper) wrapper.hidden = !visible
      this.setSectionVisibility(target, visible)

      if (target.dataset.personTypeRequired === undefined) {
        target.dataset.personTypeRequired = target.required || target.dataset.validationRequired === "true"
          ? "true"
          : "false"
      }

      if (visible) {
        const required = target.dataset.personTypeRequired === "true"
        target.required = required
        if (required) {
          target.dataset.validationRequired = "true"
        } else {
          delete target.dataset.validationRequired
        }
      } else {
        target.required = false
        delete target.dataset.validationRequired
        target.classList.remove("border-danger", "is-invalid", "is-valid")
      }
    })

    const form = this.element.closest("form") || this.element.querySelector("form")
    form?.dispatchEvent(new CustomEvent("form-validation:revalidate"))
  }

  setSectionVisibility(target, visible) {
    if (target.dataset.hideSectionWhenHidden === undefined) return

    const row = target.closest(".ui-form-table__row")
    if (!row) return

    row.hidden = !visible
    const heading = row.previousElementSibling
    if (heading?.classList.contains("ui-form-table__section-heading")) {
      heading.hidden = !visible
    }
  }

  setTargetLabel(target, text) {
    const label = target?.labels?.[0]
    if (!label) return

    const requiredMarker = label.querySelector(".text-danger")
    label.textContent = text
    if (requiredMarker) label.append(requiredMarker)
  }

  columnSpan(element) {
    return this.columnSpanFromClasses(element ? Array.from(element.classList) : [])
  }

  columnSpanFromClasses(classNames) {
    const columnClass = classNames.find((className) => /^col-md-\d+$/.test(className))
    return columnClass ? Number(columnClass.replace("col-md-", "")) : 0
  }
}
